// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import { saveAs } from 'file-saver';
import myw from 'myWorld/base/core';
import 'myWorld/base/view';
import config from 'myWorld/base/config';
import util from 'myWorld/base/util';
import 'myWorld/base/usageMonitor';

myw.System = myw.MywClass.extend('System', {
    /**
     * @class  Provides access to the information about the system (version, settings, bookmarks, permissions, etc...)
     * @constructs
     */
    initialize(server) {
        this.server = server;

        // Configuration settings @type {object}
        this.settings = null;

        this.usageMonitor = new myw.UsageMonitor(this);

        // promise that is resolved when self is ready to be used @type {promise}
        this.initialized = server.initialized.then(() => this._doInitialization());
    },

    /**
     * Performs steps that are necessary to respond to future requests.
     * Obtains settings so that getSettings can be called synchronously
     * @returns {Promise} Promise which is resolved when this object has completed
     * initialization or rejected if initialization fails.
     * If the initialization does fail, the promise is rejected with
     * a string giving the reason for failure.
     * @private
     */
    async _doInitialization() {
        if (!myw.currentUser) {
            const username = await this.server.getCurrentUsername();
            myw.currentUser = { username };
        }

        const settings = await this.getSettings().catch(error => {
            console.log(error.stack);
            error.message = 'Failed to initialize db: ' + error.message;
            throw error;
        });
        this.settings = settings;
        const systemLanguages = settings['core.language'].split(',') || [];
        this.defaultLang = systemLanguages[0] || '';
        this._convertMultiLangString = myw.Util.convertMultiLangString(
            myw.localisation.language,
            this.defaultLang,
            systemLanguages
        );
        _.extend(config, settings);

        return this;
    },

    /**
     * Localises a multi-language string
     * Uses system language and falls back to system's default language
     * @param {string|object} text
     * @param {string|object} text  (optional) text to use if language is missing
     */
    localise(text, missing_language_text) {
        if (!myw.localisation) throw new Error('Localisation not available.');
        return this._convertMultiLangString(text, missing_language_text);
    },

    /**
     * Registers the error handler for promises
     * @param  {Function} errorHandler function()
     */
    registerErrorHandler(errorHandler) {
        this.server._errorHandler = errorHandler; //ENH: improve this
    },

    /**
     * Returns the schema version of the database
     * @return {Promise<integer>}
     */
    getSchemaVersion() {
        return this.server.getSchemaVersion();
    },

    /**
     * Check whether the database is a replica or not
     * @return {boolean}     True if the database is a replica
     */
    isReplicaDatabase() {
        return this.server.isReplicaDatabase();
    },

    /**
     * Returns whether a layer is included in the current extract or not
     * @param  {layerDefinition}  layerDef
     * @return {Boolean}
     */
    isLayerExtracted(layerDef) {
        return this.server.isLayerExtracted(layerDef);
    },

    /* ****************************** Application methods **************************** */

    /**
     * Get startup details for a given application
     * @param  {string} applicationName [description]
     * @return {Promise<object>}    Promise for layer, datasource and layer group definitions
     */
    getStartupInfo(applicationName) {
        //setup a cache for requests
        if (!this._startupRequest) this._startupRequest = {};

        if (!this._startupRequest[applicationName]) {
            //send the request and cache it
            this._startupRequest[applicationName] = this.server
                .getStartupInfo(applicationName)
                .then(this._processStartupInfo.bind(this));
        }
        return this._startupRequest[applicationName];
    },

    /**
     * Get admin notifications
     * @param {integer} sinceId  Notifications ID that marks the start of the notifications to show
     */
    getAdminNotifications(sinceId) {
        return this.server.getAdminNotifications(sinceId);
    },

    /* ****************************** Layers methods **************************** */
    /**
     * Get the layer definition for a given layer name
     * @param  {string} layerName     Name of the layer
     * @return {Promise<layerDefinition>}    Promise for the specified layer definition
     */
    getLayerWithName(layerName) {
        return this.server.getLayerWithName(layerName);
    },

    /**
     * Get all the layer groups
     * @return {Promise<Array<layerGroupItem>>}  Promise for the layer groups
     */
    getLayerGroups() {
        return this.server.getLayerGroups();
    },

    /**
     * Saves a given private layer definition to the database
     * @param  {privateLayerDef} privateLayerDef
     * @return {promise}
     */
    savePrivateLayer(privateLayerDef) {
        return this.server.savePrivateLayer(privateLayerDef);
    },

    /**
     * Saves a given private layer definition to the database
     * @param  {string} id
     * @return {promise}
     */
    deletePrivateLayer(id) {
        return this.server.deletePrivateLayer(id);
    },

    /**
     * Sends a request to an external datasource via the myWorld server<br/>
     * Uses the url specified in the datasource definition
     * @param  {string}     dsName          Datasource name
     * @param  {Object}     requestParams   Key/value pairs that will be passed on as url parameters
     * @param  {string}     [options.urlFieldName='url']    Name of property in datasource's spec that holds the base url for the request
     * @param  {string}     [options.relativeUrl='']        Relative url to append to the base url
     * @return {Promise<Object>}  Promise for the results (parsed from json)
     */
    tunnelDatasourceRequest(dsName, requestParams, options) {
        //handle default values
        options = _.extend(
            {
                urlFieldName: 'url',
                relativeUrl: ''
            },
            options
        );

        return this.server.tunnelDatasourceRequest(dsName, requestParams, options);
    },

    buildTunnelDatasourceRequestUrl(dsName, requestParams, options) {
        return this.server.buildTunnelDatasourceRequestUrl(dsName, requestParams, options);
    },

    /**
     * Returns the networks a given feature can be part of
     * @param  {MyWorldFeature} feature
     * @return {promise}         Network definition keyed on network name
     */
    getNetworksFor(feature) {
        return this.server.getNetworksFor(feature);
    },

    /* ****************************** Bookmarks methods **************************** */
    /**
     * Get the details of a bookmark
     * @param  {integer} id     The id of the bookmark
     * @return {Promise<bookmark>}        Promise for the bookmark details
     */
    getBookmark(id) {
        return this.server.getBookmark(id);
    },

    /**
     * Gets the bookmark with the given title
     * @param  {string} title  Title of the bookmark
     * @return {Promise<bookmark>}        Promise for the bookmark details
     */
    getBookmarkByTitle(title) {
        return this.server.getBookmarkByTitle(title);
    },

    /**
     * Gets the bookmarks the user has access to
     * @return {Promise<bookmark[]>}        Promise for the list of bookmark details
     */
    getBookmarksForUser() {
        return this.server.getBookmarksForUser();
    },

    /**
     * Creates a bookmark or if the current user has one with the same name, replaces it
     * @param  {Object}     bookmarkData Bookmark data
     * @return {Promise}    Promise which will resolve when the operation completes
     */
    saveBookmark(bookmarkData) {
        delete bookmarkData.geometry; //bookmark doesn't include a geometry anymore
        return this.server.saveBookmark(bookmarkData);
    },

    /**
     * Delete a bookmark by it's id
     * @param  {integer}   bookmarkId
     * @return {Promise}   Promise which will resolve when the operation completes
     */
    deleteBookmark(bookmarkId) {
        return this.server.deleteBookmark(bookmarkId);
    },

    /**
     * Update a bookmark
     * @param  {integer}    bookmarkId
     * @param  {Object}     bookmarkData Bookmark data
     * @return {Promise}    Promise which will resolve when the operation completes
     */
    updateBookmark(bookmarkId, bookmarkData) {
        return this.server.updateBookmark(bookmarkId, bookmarkData);
    },

    /* ****************************** Groups **************************** */

    /**
     * get the groups accessible to the current user
     * @param  {Boolean} [isManager]    whether current user is a manager of the group or not
     * @return {Promise<groupIds[]>}    Promise for the list of group ids
     */
    getGroupsIds(isManager) {
        isManager = !!isManager;
        return this.server.getGroupsIds(isManager);
    },

    /**
     * get the groups definition for the id
     * @param  {string}    id    group id
     * @return {Promise<Object>}    Promise for the object containing group definition
     */
    getGroup(id) {
        return this.server.getGroup(id);
    },

    /**
     * Creates a group
     * @param  {Object}     groupData Bookmark data
     * @return {Promise}    Promise which will resolve when the operation completes
     */
    saveGroup(groupData) {
        return this.server.saveGroup(groupData);
    },

    /**
     * Delete a group by it's id
     * @param  {integer}   groupId
     * @return {Promise}   Promise which will resolve when the operation completes
     */
    deleteGroup(groupId) {
        return this.server.deleteGroup(groupId);
    },

    /**
     * Update a group
     * @param  {integer}    groupId
     * @param  {Object}     groupData Group data
     * @return {Promise}    Promise which will resolve when the operation completes
     */
    updateGroup(groupId, groupData) {
        return this.server.updateGroup(groupId, groupData);
    },

    /* ****************************** Usage monitor **************************** */
    /**
     * Record licence usage
     * @param  {string} application
     * @param  {string} operation
     */
    consumeLicence(application, operation) {
        this.usageMonitor.log('licence', application, operation);
    },

    /**
     * Record functionality usage
     * @param  {string} application
     * @param  {string} operation
     */
    recordFunctionalityAccess(application, operation) {
        this.usageMonitor.log('functionality', application, operation);
    },

    /**
     * Record data usage
     * @param  {string} application
     * @param  {string} operation
     */
    recordDataAccess(application, operation) {
        this.usageMonitor.log('data', application, operation);
    },

    getUsageMonitorSettings() {
        return this.server.getUsageMonitorSettings();
    },

    createUsageMonitorSession(data) {
        return this.server.createUsageMonitorSession(data);
    },

    updateUsageMonitorSession(id, data) {
        return this.server.updateUsageMonitorSession(id, data);
    },

    /* ****************************** System settings **************************** */

    /**
     * Get the application settings stored in the database
     * @return {Promise<Object>} A promise for the settings (an object keyed on setting name)
     */
    getSettings() {
        if (!this._settingsRequest) {
            this._settingsRequest = this.server.getSettings().then(settingsArray => {
                const settings = {};

                _.each(settingsArray, record => {
                    let value;

                    try {
                        value = this._convertApplicationSettingValue(record.value, record.type);
                        settings[record.name] = value;
                    } catch (e) {
                        throw new Error(
                            "Failure to convert application setting '" +
                                record.name +
                                "'': " +
                                e.message
                        );
                    }
                });
                return settings;
            });
        }
        return this._settingsRequest;
    },

    /* ****************************** Permissions methods **************************** */

    /**
     * Check if the current user has permission for a given right
     * @param  {string} right           Name of the right to check for
     * @return {Promise<boolean>}       Promise for whether the current user has permission or not
     */
    userHasPermission(right) {
        return this.getUserPermissions().then(
            permissions => !!permissions[myw.app.name][right],
            reason => {
                console.log('userHasPermission failed because:', reason);
                return false;
            }
        );
    },

    /**
     * Obtain a list of the current user's permissions
     * @return {Promise<permissions>} Promise for the lists of rights per application
     */
    getUserPermissions() {
        if (!this._userPermissions) {
            this._userPermissions = this.server.getUserPermissions();
        }
        return this._userPermissions;
    },

    /**
     * Obtain a url for kml
     * @param  {layerDef} layerDef
     * @return {Promise<string>} Promise for the url string
     */
    getUrlForKml(layerDef) {
        return this.server.getUrlForKml(layerDef);
    },

    /**
     * Obtain a url for kmz
     * @param  {layerDef} layerDef
     * @param  {string} filename
     * @return {Promise<string>} Promise for the url string
     */
    getUrlForKmz(layerDef, filename = null) {
        return this.server.getUrlForKmz(layerDef, filename);
    },

    /* ****************************** Applications methods **************************** */

    /**
     * Return the application details of the applications the current user has access to
     * The userPermissions gets the application Ids the user has access to
     * The getAllApplications() method is used to get application details of
     * all applications
     * @return {Promise} Promise for a list of application's details
     */
    getUserApplications() {
        return Promise.join(
            this.getUserPermissions(),
            this.getAllApplications(),
            (userPermissions, allApplications) =>
                _.filter(allApplications, anApp => {
                    //filters out applications for which there is no rights/permissions
                    const validEnvironment =
                        (anApp.for_online_app && !myw.isNativeApp) ||
                        (anApp.for_native_app && myw.isNativeApp);
                    const appPerms = userPermissions[anApp.name];
                    const hasAccess = appPerms && appPerms['accessApplication'];
                    return validEnvironment && hasAccess;
                })
        );
    },

    /**
     * Obtains a list with all application records
     * @return {Promise} Promise for a list of application's details
     */
    getAllApplications() {
        if (!this._allApplications) {
            //cache the promise
            this._allApplications = this.server.getAllApplications();
        }
        return this._allApplications;
    },

    /**
     * Saves the session state for a given application name
     * @param  {string} name                Name of the application to associate with the given state
     * @param  {boolean} [persist=false]    Whether state should persist for use in other machines/browsers
     * @param  {object} state
     */
    saveApplicationState(name, state, persist = false) {
        //clear get cache, as it has become invalid/out-of-date
        if (this._savedApplicationsState) delete this._savedApplicationsState[name];

        return this.server.saveApplicationState(name, state, persist);
    },

    /**
     * Obtains the last saved state of an application's session
     * @param  {string} name                        Name of the application
     * @param  {boolean} ignoreBrowserSavedState    Whether to obtain from local storage or only from database
     * @return {object}
     */
    getSavedApplicationState(name, ignoreBrowserSavedState) {
        return this.server.getSavedApplicationState(name, ignoreBrowserSavedState);
    },

    /**
     * Saves the session state that is common/shared for all applications
     * @param  {object} state
     */
    saveSharedState(state) {
        //save as if it was an application using an invalid application name
        _.extend(state, { usageMonitor: this.usageMonitor.getState() });
        return this.server.saveApplicationState('!!!shared!!!', state, false);
    },

    /**
     * Obtain the last shared/common (across applications) state
     * @param  {boolean} ignoreBrowserSavedState    Whether to obtain from local storage or only from database
     * @return {object}
     */
    getSavedSharedState(ignoreBrowserSavedState) {
        return this.server.getSavedApplicationState('!!!shared!!!', ignoreBrowserSavedState);
    },

    /**
     * Generates a file to be downloaded by the browser
     * @param  {string}     format    'json' or 'csv'
     * @param  {json}       data
     * @return {promise<string>}  Url for the file
     */
    export(format, data) {
        return this.server.export(format, data);
    },

    /**
     * Upload local changes for given datasource
     * @param {IDatasource} datasource   The datasource
     * @ return {promise}  Resolves when the upload is complete
     * Only for use in the Native App with the 'myworld' datasource. If called for other cases
     * an error will be thrown.
     */
    uploadLocalChanges(datasource) {
        if (myw.isNativeApp) {
            if (datasource.name === 'myworld') {
                return datasource.initialized.then(() =>
                    this.server.uploadLocalChanges(datasource.masterDs.server)
                );
            } else {
                throw new Error("uploadLocalChanges() will only run on the 'myworld' datasource");
            }
        } else {
            throw new Error('uploadLocalChanges() will only run in the Native App');
        }
    },

    /**
     * Start a download on the client
     * @param {Blob} blob file object
     * @param {string} filename to be used in the download
     * @param {string} mimeType to encode the download with
     */
    executeBlobDownload(blob, filename, mimeType) {
        if (myw.isNativeApp) {
            this.server.executeBlobDownload(blob, filename, mimeType);
        } else if (navigator.userAgent.match('CriOS')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                window.open(reader.result);
            };
            reader.readAsDataURL(blob);
        } else {
            saveAs(blob, filename);
        }
    },

    /* ******************************** Auxiliary methods ****************************** */

    async _processStartupInfo(result) {
        //override id of system layers with the layer name
        //this provides a key which doesn't clash with private layer's id and avoids
        //having to upgrade saved layer list state
        //Also localise the display names and description
        await this.initialized;

        result.datasources.forEach(dsDef => {
            Object.keys(dsDef.featureTypes).forEach(featureTypeName => {
                const featureDef = dsDef.featureTypes[featureTypeName];
                featureDef.external_name = this.localise(featureDef.external_name, featureTypeName);
            });
        });

        result.layers.forEach(layerDef => {
            layerDef.id = layerDef.name;
            layerDef.display_name = this.localise(layerDef.display_name, layerDef.name);
            layerDef.description = this.localise(layerDef.description, '');
        });

        result.layerGroups.forEach(group => {
            group.display_name = this.localise(group.display_name, group.name);
            group.description = this.localise(group.description, '');
        });

        //create a datasource entry and layer entry for each user layer definition
        result.privateLayers.forEach(privateLayerDef => {
            result.datasources.push({
                name: privateLayerDef.id,
                external_name: `${privateLayerDef.name} (${privateLayerDef.owner})`,
                owner: privateLayerDef.owner,
                ...privateLayerDef.datasource_spec
            });
            privateLayerDef.datasource = privateLayerDef.id;
            result.layers.push(privateLayerDef);
        });

        //move properties stored in spec as json to the definition itself
        const allDefs = result.datasources.concat(result.layers);
        _.each(allDefs, def => {
            _.extend(def, def.spec);
            delete def.spec;
        });
        return result;
    },

    _convertApplicationSettingValue(value, type) {
        switch (type) {
            case 'JSON':
                value = JSON.parse(value);
                break;

            case 'INTEGER':
                value = parseInt(value, 10);
                break;

            default:
                // No conversion
                break;
        }
        return value;
    }
});

/**
 * A universal identifier
 * @typedef urn
 * @property {string} tableName The table name (i.e. feature type)
 * @property {string} id        Record identifier
 */

/**
 * The details of a bookmark
 * @typedef bookmark
 * @property {number} lat           Latitude of the centre of the bookmark
 * @property {number} lng           Longitude of the centre of the bookmark
 * @property {integer} zoom         Zoom level to use
 * @property {string} map_display   Layers to use. Format is &lt;basemap name>|&lt;comma separated layer codes>
 */

/**
 * Layer definition. Additional properties may be present depending on the datasource type
 * @typedef layerDefinition
 * @property {string}             id            Unique Id of the layer
 * @property {string}             type          One of: myworld_tile, myworld_vector, leaflet, custom, google, kml, wms, myworld_tile_directory, mbtiles
 * @property {string}             category      'overlay' or 'basemap'
 * @property {string}             name          Name of the layer
 * @property {string}             code          Unique one character code for the layer
 * @property {string}             description   Description of the layer
 * @property {string}             thumbnail     Relative url for a thumbnail image
 * @property {string}             min_scale     Minimum scale at which the layer should be drawn on the client map
 * @property {string}             max_scale     Maximum scale at which the layer should be  drawn on the client map
 * @property {string}             attribution   'leaflet' tile layers:  the string used by the attribution control, describes the layer data.
 * @property {string}             datasource    Name of datasource for the layer
 * @property {layerFeatureItem[]} feature_types Layer feature objects describing the features appearing on this layer.
 */

/**
 * Item of a layer list. Layer list is a list/configuration of layers for a user/application pair
 * @typedef {(layerListLayer|layerListGroup|layerListGroupItem)} layerListItem
 */

/**
 * @typedef layerListLayer
 * @property {string}           layer_name             Name of the layer
 * @property {string}           [type="layer"]   Defines the type in the user's layerList
 * @property {integer}          sequence               Order of the group amongst other groups and layers
 * @property {boolean}          turned_on              Whether the group is turned on (to be shown on the map) or not
 * @property {layerDefinition}  layerDef
 */

/**
 * @typedef layerListGroup
 * @property {string}                     layer_name             Name of the layer group
 * @property {string}                     [type="layer_group"]   Defines the type in the user's layerList
 * @property {integer}                    sequence               Order of the group amongst other groups and layers
 * @property {boolean}                    turned_on              Whether the group is turned on (to be shown on the map) or not
 * @property {boolean}                    exclusive              Whether the group's layers can only be exclusively selected or not
 * @property {string}                     thumbnail              Thumbnail image source path
 * @property {array<layerListGroupItem>}  subLayers              Layers that are assigned to this group
 */

/**
 * @typedef layerListGroupItem
 * @property {string}           layer_name                  Name of the layer
 * @property {string}           [type="layer_group_item"]   Defines the type in the user's layerList
 * @property {integer}          sequence                    Order of the group amongst other groups and layers
 * @property {integer}          subsequence                 Order of the layer amongst other layers in the group
 * @property {boolean}          turned_on                   Whether the group is turned on (to be shown on the map) or not
 * @property {layerDefinition}  layerDef
 */

/**
 * Rights per application. Keyed on application name
 * @typedef {Object<applicationRights>} permissions
 */

/**
 * Rights for an application. Keyed on right name. An absent value name that right has not been granted.
 * @typedef {Object<boolean>} applicationRights
 */

/**
 * Describes the style of a feature on a layer
 * @typedef layerFeatureItem
 * @property {integer}   layer_id     Id of layer this item applies to
 * @property {integer}   feature_id   Id of feature this item applies to
 * @property {string}    point_style  Point style of this eature on this layer
 * @property {string}    line_style   Point style of this eature on this layer
 * @property {string}    fill_style   Point style of this eature on this layer
 */

/**
 * Parameters for a render request that obtains feature information suitable for vector rendering
 * @typedef renderParams
 * @property  {string}         layerName
 * @property  {Array<L.LatLngBounds>|L.LatLngBounds} bounds      Single or List of bounding boxes to filter the results on
 * @property  {integer}        [limit]     Max number of records to return. Default comes from configuration.
 * @property  {string}         [world_name=geo]  World to render
 * @property  {string}         [offset]   String to be treated as opaque by client that indicates to server where to resume query on second and subsequent requests
 * @property  {string}         [schema=data]    If 'delta' only features in a delta are returned
 */

export default myw.System;
